import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import './ListaMaterial.css'

/** Administrativo desta empresa: mesma visão ampla que administrador (listas de todas as empresas). */
const EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

const ListaMaterial = ({ onBack, userRole, userEmpresa }) => {
  /** Listas/clientes: administrador vê tudo; administrativo só com empresa Clarear vê tudo; demais perfis só da própria empresa. */
  const podeVerTodosRegistrosListaMaterial =
    userRole === 'administrador' ||
    (userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS)

  // Função para formatar valores monetários no padrão brasileiro
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00'
    }
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Função para formatar valores sem o símbolo R$ (apenas números)
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0,00'
    }
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const [clientes, setClientes] = useState([])
  const [selectedCliente, setSelectedCliente] = useState('')
  const [materialData, setMaterialData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [clientSearchTerm, setClientSearchTerm] = useState('')
  const [showClientSuggestions, setShowClientSuggestions] = useState(false)
  const [filteredClients, setFilteredClients] = useState([])
  // Estados para seleção de Ordem de Serviço (nova origem da lista de material)
  const [ordensServico, setOrdensServico] = useState([])
  const [selectedOrdemServico, setSelectedOrdemServico] = useState('')
  const [osSearchTerm, setOsSearchTerm] = useState('')
  const [showOsSuggestions, setShowOsSuggestions] = useState(false)
  const [filteredOrdens, setFilteredOrdens] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [listaToDelete, setListaToDelete] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [previewData, setPreviewData] = useState([])
  // Estados para gerenciar listas existentes
  const [listasExistentes, setListasExistentes] = useState([])
  const [selectedLista, setSelectedLista] = useState('')
  const [showExistingListas, setShowExistingListas] = useState(false)
  
  // Estados para controlar edição vs nova lista
  const [isEditing, setIsEditing] = useState(false)
  const [editingListaId, setEditingListaId] = useState(null)
  const [editingClienteId, setEditingClienteId] = useState(null)
  const [editingOrdemServicoId, setEditingOrdemServicoId] = useState(null)
  
  // Estado para dados da empresa
  const [empresaData, setEmpresaData] = useState(null)

  // Estado para searchbox de material abaixo do botão + Adicionar Item
  const [materialSearchTerm, setMaterialSearchTerm] = useState('')
  const [materialSuggestions, setMaterialSuggestions] = useState([])
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false)
  const [materialSearchLoading, setMaterialSearchLoading] = useState(false)
  const [manualMaterialQuantidade, setManualMaterialQuantidade] = useState(1)
  const materialSearchRef = useRef(null)

  const isAdministrador = userRole === 'administrador'
  const [showModalCatalogoPreco, setShowModalCatalogoPreco] = useState(false)
  const [catalogoPrecoItens, setCatalogoPrecoItens] = useState([])
  const [catalogoPrecoLoading, setCatalogoPrecoLoading] = useState(false)
  const [catalogoPrecoSaving, setCatalogoPrecoSaving] = useState(false)
  const [catalogoForm, setCatalogoForm] = useState({ id: null, material: '', preco_unitario: '' })

  // Carregar clientes e dados da empresa ao montar e quando a empresa do usuário mudar
  useEffect(() => {
    fetchClientes()
    fetchOrdensServico()
    fetchListasExistentes()
    fetchEmpresaData()
  }, [userEmpresa?.id, userRole])

  // Sincronizar searchbox com cliente selecionado (apenas quando não está editando)
  useEffect(() => {
    if (selectedCliente && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === selectedCliente)
      if (cliente) {
        setClientSearchTerm(cliente.nome_cliente)
      }
    } else if (!selectedCliente) {
      setClientSearchTerm('')
    }
  }, [selectedCliente, clientes])

  const fetchClientes = async () => {
    try {
      setIsLoading(true)
      setMessage('Carregando clientes...')
      setMessageType('info')
      
      // Buscar clientes apenas da tabela 'clientes_cadastro'
      let queryCadastro = supabase
        .from('clientes_cadastro')
        .select('id, nome_completo, empresa_id')
        .not('nome_completo', 'is', null)
      
      // Filtrar por empresa (exceto administrador ou administrativo da empresa acima)
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        queryCadastro = queryCadastro.eq('empresa_id', userEmpresa.id)
      }
      
      const { data: cadastroData, error: cadastroError } = await queryCadastro

      if (cadastroError) {
        throw cadastroError
      }
      
      // Mapear os dados para o formato esperado (usar nome_completo como nome_cliente)
      const clientes = (cadastroData || []).map(c => ({
        id: c.id,
        nome_cliente: c.nome_completo
      }))
      
      // Ordenar por nome
      clientes.sort((a, b) => {
        const nomeA = (a.nome_cliente || '').toLowerCase()
        const nomeB = (b.nome_cliente || '').toLowerCase()
        return nomeA.localeCompare(nomeB)
      })

      console.log('Total de clientes carregados:', clientes.length, 'clientes')
      if (clientes.length > 0) {
        console.log('Primeiros 5 clientes:', clientes.slice(0, 5))
      }

      if (clientes.length === 0) {
        setMessage('Nenhum cliente encontrado.')
        setMessageType('warning')
        setClientes([])
      } else {
        setMessage(`${clientes.length} clientes carregados com sucesso!`)
        setMessageType('success')
        setClientes(clientes)
      }
      
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      setMessage(`Erro ao carregar clientes: ${error.message}`)
      setMessageType('error')
      setClientes([])
    } finally {
      setIsLoading(false)
    }
  }

  // Carregar ordens de serviço (nova origem da lista de material). Uma lista é criada a partir de uma OS.
  const fetchOrdensServico = async () => {
    try {
      let query = supabase
        .from('ordens_servicos')
        .select(`
          id,
          id_cliente,
          id_tipo_servico,
          id_empresa,
          status,
          data_execucao,
          data_criacao,
          cliente_cadastro:clientes_cadastro!id_cliente(nome_completo),
          tipo_servico(nome)
        `)
        .order('data_criacao', { ascending: false })

      // Mesma regra de visibilidade das listas: administrador (e administrativo da Clarear) veem tudo.
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        query = query.eq('id_empresa', userEmpresa.id)
      }

      const { data, error } = await query
      if (error) throw error

      const ordens = (data || [])
        .filter(os => (os.status || '') !== 'Cancelado')
        .map(os => {
          const nomeCliente = os.cliente_cadastro?.nome_completo || 'Cliente sem nome'
          const tipoNome = os.tipo_servico?.nome || 'Serviço'
          const dataFmt = os.data_execucao
            ? new Date(os.data_execucao + 'T00:00:00').toLocaleDateString('pt-BR')
            : (os.data_criacao ? new Date(os.data_criacao).toLocaleDateString('pt-BR') : 's/ data')
          return {
            id: os.id,
            id_cliente: os.id_cliente,
            nome_cliente: nomeCliente,
            tipo_servico: tipoNome,
            status: os.status,
            data_execucao: os.data_execucao,
            label: `OS #${os.id} — ${nomeCliente} — ${tipoNome} (${dataFmt})`
          }
        })

      setOrdensServico(ordens)
    } catch (error) {
      console.error('Erro ao carregar ordens de serviço:', error)
      setOrdensServico([])
    }
  }

  // Função para carregar dados da empresa (sempre da empresa do usuário logado para PDF e tela)
  const fetchEmpresaData = async () => {
    try {
      // Priorizar sempre a empresa do usuário logado (userEmpresa), para qualquer perfil
      let empresaId = userEmpresa?.id || null
      if (!empresaId) {
        // Fallback: para admin sem empresa selecionada, buscar a primeira empresa ativa
        const { data: empresas, error: empresasError } = await supabase
          .from('empresa')
          .select('id')
          .eq('ativa', true)
          .limit(1)
        if (!empresasError && empresas && empresas.length > 0) {
          empresaId = empresas[0].id
        }
      }

      if (!empresaId) {
        console.warn('Nenhuma empresa encontrada para carregar dados')
        setEmpresaData(null)
        return
      }

      const { data: empresa, error: empresaError } = await supabase
        .from('empresa')
        .select('empresa, razao_social, cnpj, endereco, email, contato, cidade, estado, cep')
        .eq('id', empresaId)
        .single()

      if (empresaError) throw empresaError

      setEmpresaData(empresa)
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error)
      setEmpresaData(null)
    }
  }

  const fetchListasExistentes = async () => {
    try {
      
      // Buscar TODAS as listas de material (incluindo listas antigas)
      let query = supabase
        .from('lista_material')
        .select(`
          id,
          data_criacao,
          observacoes,
          total_resolve,
          total_fornecido,
          cliente_id,
          id_ordem_servico
        `)
        .order('data_criacao', { ascending: false })

      const { data: todasListas, error: errorListas } = await query

      if (errorListas) {
        console.error('❌ Erro ao carregar listas existentes:', errorListas)
        return
      }
      
      if (!todasListas || todasListas.length === 0) {
        setListasExistentes([])
        return
      }
      
      // Buscar clientes correspondentes de clientes_cadastro
      const clienteIdsUnicos = [...new Set(todasListas.map(l => l.cliente_id).filter(Boolean))]
      
      let clientesMap = new Map()
      
      if (clienteIdsUnicos.length > 0) {
        let clientesQuery = supabase
          .from('clientes_cadastro')
          .select('id, nome_completo, empresa_id')
          .in('id', clienteIdsUnicos)
        
        const { data: clientesData, error: clientesError } = await clientesQuery
        
        if (!clientesError && clientesData) {
          clientesData.forEach(c => {
            clientesMap.set(c.id, c)
          })
        }
      }
      
      // Filtrar listas por empresa do usuário (exceto visão ampla)
      let listasFiltradas = todasListas
      
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        listasFiltradas = todasListas.filter(lista => {
          const cliente = clientesMap.get(lista.cliente_id)
          // Se o cliente existe e tem empresa_id, filtrar pela empresa
          if (cliente && cliente.empresa_id) {
            return cliente.empresa_id === userEmpresa.id
          }
          // Se o cliente não foi encontrado, incluir a lista (pode ser antiga)
          // Ou você pode optar por excluir: return false
          return true // Incluir listas antigas sem cliente correspondente
        })
      }
      
      // Adicionar informações do cliente manualmente
      const listasComClientes = listasFiltradas.map(lista => {
        const cliente = clientesMap.get(lista.cliente_id)
        return {
          ...lista,
          clientes: cliente 
            ? { nome_cliente: cliente.nome_completo } 
            : { nome_cliente: `Cliente ID: ${lista.cliente_id?.substring(0, 8)}... (não encontrado)` }
        }
      })
      
      console.log('📚 Listas carregadas:', listasComClientes.length, 'listas')
      setListasExistentes(listasComClientes)
    } catch (error) {
      console.error('❌ Erro ao carregar listas existentes:', error)
    }
  }

  // Funções para gerenciar o searchbox de clientes
  const handleClientSearch = (term) => {
    setClientSearchTerm(term)
    
    if (term.length === 0) {
      setFilteredClients([])
      setShowClientSuggestions(false)
      setSelectedCliente('')
      return
    }
    
    const filtered = clientes.filter(cliente => 
      cliente.nome_cliente.toLowerCase().includes(term.toLowerCase())
    )
    
    setFilteredClients(filtered)
    setShowClientSuggestions(filtered.length > 0)
    
    // Se o termo digitado corresponde exatamente a um cliente, selecionar automaticamente
    const exactMatch = clientes.find(cliente => 
      cliente.nome_cliente.toLowerCase() === term.toLowerCase()
    )
    if (exactMatch) {
      setSelectedCliente(exactMatch.id)
    } else {
      setSelectedCliente('')
    }
  }

  const handleClientSelect = (cliente) => {
    setSelectedCliente(cliente.id)
    setClientSearchTerm(cliente.nome_cliente)
    setShowClientSuggestions(false)
    setFilteredClients([])
  }

  const handleClientSearchBlur = () => {
    // Aguardar mais tempo para permitir clique na sugestão
    setTimeout(() => {
      setShowClientSuggestions(false)
      setFilteredClients([])
    }, 300)
  }

  const handleClientSearchFocus = () => {
    if (clientSearchTerm.length > 0) {
      handleClientSearch(clientSearchTerm)
    }
  }

  const handleClientSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredClients.length > 0) {
      e.preventDefault()
      handleClientSelect(filteredClients[0])
    } else if (e.key === 'Escape') {
      setShowClientSuggestions(false)
      setFilteredClients([])
    }
  }

  // Searchbox de Ordem de Serviço (substitui a escolha direta do cliente ao criar nova lista)
  const handleOsSearch = (term) => {
    setOsSearchTerm(term)

    if (term.length === 0) {
      setFilteredOrdens([])
      setShowOsSuggestions(false)
      setSelectedOrdemServico('')
      setSelectedCliente('')
      return
    }

    const t = term.toLowerCase()
    const filtered = ordensServico.filter(os =>
      os.label.toLowerCase().includes(t) ||
      os.nome_cliente.toLowerCase().includes(t) ||
      String(os.id).includes(t)
    )

    setFilteredOrdens(filtered)
    setShowOsSuggestions(filtered.length > 0)
    setSelectedOrdemServico('')
    setSelectedCliente('')
  }

  const handleOsSelect = (os) => {
    setSelectedOrdemServico(os.id)
    setSelectedCliente(os.id_cliente)
    setOsSearchTerm(os.label)
    setShowOsSuggestions(false)
    setFilteredOrdens([])
  }

  const handleOsSearchBlur = () => {
    setTimeout(() => {
      setShowOsSuggestions(false)
      setFilteredOrdens([])
    }, 300)
  }

  const handleOsSearchFocus = () => {
    if (osSearchTerm.length > 0) {
      handleOsSearch(osSearchTerm)
    } else {
      setFilteredOrdens(ordensServico.slice(0, 50))
      setShowOsSuggestions(ordensServico.length > 0)
    }
  }

  const handleOsSearchKeyDown = (e) => {
    if (e.key === 'Enter' && filteredOrdens.length > 0) {
      e.preventDefault()
      handleOsSelect(filteredOrdens[0])
    } else if (e.key === 'Escape') {
      setShowOsSuggestions(false)
      setFilteredOrdens([])
    }
  }

  // Catálogo: itens_material_preco filtrado por id_empresa = empresa do usuário logado (equivalente a LIKE no material)
  const buscarMateriaisSugestoes = async (termo) => {
    try {
      const empresaId = userEmpresa?.id
      if (!empresaId) {
        console.warn('buscarMateriaisSugestoes: usuário sem empresa no perfil.')
        return []
      }

      let query = supabase
        .from('itens_material_preco')
        .select('id, material, preco_unitario')
        .eq('id_empresa', empresaId)
        .order('material', { ascending: true })
        .limit(200)
      const t = termo != null ? String(termo).trim() : ''
      if (t) {
        query = query.ilike('material', `%${t}%`)
      }
      const { data: rows, error } = await query
      if (error) {
        console.error('buscarMateriaisSugestoes:', error)
        return []
      }

      const seen = new Set()
      const result = []
      for (const row of rows || []) {
        const mat = (row.material || '').trim()
        if (!mat || seen.has(mat.toLowerCase())) continue
        seen.add(mat.toLowerCase())
        result.push({
          material: mat,
          valor_unitario: row.preco_unitario != null ? Number(row.preco_unitario) : 0
        })
        if (result.length >= 30) break
      }
      return result
    } catch (e) {
      console.error('buscarMateriaisSugestoes:', e)
      return []
    }
  }

  // Ao selecionar material no searchbox (abaixo do botão): adicionar nova linha no Preview com material e valor (catálogo)
  const handleAddMaterialFromSearch = (material, valor_unitario) => {
    const qtd = Math.max(1, parseInt(String(manualMaterialQuantidade), 10) || 1)
    const newItem = {
      material: material || '',
      quantidade: qtd,
      classe: 'Nenhum',
      valor_unitario: valor_unitario != null ? Number(valor_unitario) : 0,
      valor_historico_carregado: true,
      resolve_forneceu: false,
      fornecido_forneceu: true
    }
    if (isEditing && editingListaId) {
      newItem.lista_material_id = editingListaId
    }
    setMaterialData([...materialData, newItem])
    setPreviewData([...previewData, newItem])
    setShowPreview(true)
    setShowMaterialDropdown(false)
    setMaterialSearchTerm('')
    setMaterialSuggestions([])
    setManualMaterialQuantidade(1)
  }

  // Abrir dropdown e carregar sugestões ao focar no campo de busca abaixo do botão
  const handleMaterialSearchFocus = () => {
    setShowMaterialDropdown(true)
    setMaterialSearchLoading(true)
    buscarMateriaisSugestoes(materialSearchTerm).then((list) => {
      setMaterialSuggestions(list)
      setMaterialSearchLoading(false)
    })
  }

  // Ao digitar no campo de busca abaixo do botão
  const handleMaterialSearchChange = (value) => {
    setMaterialSearchTerm(value)
    setShowMaterialDropdown(true)
    setMaterialSearchLoading(true)
    buscarMateriaisSugestoes(value).then((list) => {
      setMaterialSuggestions(list)
      setMaterialSearchLoading(false)
    })
  }

  // Fechar dropdown ao clicar fora (useEffect com listener no document)
  useEffect(() => {
    if (!showMaterialDropdown) return
    const close = (e) => {
      if (materialSearchRef.current && !materialSearchRef.current.contains(e.target)) {
        setShowMaterialDropdown(false)
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showMaterialDropdown])

  // Função para calcular total geral (todos os itens são fornecidos)
  const calculateTotals = () => {
    let totalGeral = 0
    materialData.forEach((item) => {
      totalGeral += (Number(item.valor_unitario) || 0) * (Number(item.quantidade) || 0)
    })
    return { totalGeral }
  }


  // Função para salvar lista de material
  const handleSaveLista = async () => {
    // Verificar se temos dados válidos
    if (materialData.length === 0) {
      setMessage('Adicione itens de material antes de salvar')
      setMessageType('error')
      return
    }

    // Verificar ordem de serviço / cliente
    let clienteId = selectedCliente
    let ordemServicoId = selectedOrdemServico
    if (isEditing) {
      clienteId = editingClienteId
      ordemServicoId = editingOrdemServicoId
    } else {
      const osSelecionada = ordensServico.find(o => o.id === selectedOrdemServico)
      if (!osSelecionada) {
        setMessage('Selecione uma ordem de serviço para salvar a lista')
        setMessageType('error')
        return
      }
      clienteId = osSelecionada.id_cliente
      ordemServicoId = osSelecionada.id
    }

    if (!clienteId) {
      setMessage('Não foi possível identificar o cliente da ordem de serviço selecionada')
      setMessageType('error')
      return
    }

    try {
      setIsLoading(true)
      setMessage('Salvando lista de material...')
      setMessageType('info')

      console.log('💾 Iniciando salvamento da lista. Cliente ID:', clienteId)

      const { totalGeral } = calculateTotals()

      if (isEditing && editingListaId) {
        // ATUALIZAR LISTA EXISTENTE
        setMessage('Atualizando lista existente...')
        
        // Atualizar a lista principal
        const { error: listaError } = await supabase
          .from('lista_material')
          .update({
            observacoes: `Editado em ${new Date().toLocaleDateString('pt-BR')}`,
            total_resolve: 0,
            total_fornecido: totalGeral
          })
          .eq('id', editingListaId)

        if (listaError) throw listaError

        // Excluir itens antigos
        const { error: deleteError } = await supabase
          .from('itens_material')
          .delete()
          .eq('lista_material_id', editingListaId)

        if (deleteError) throw deleteError

        // Inserir novos itens
        const itensToInsert = materialData.map((item) => ({
          lista_material_id: editingListaId,
          material: item.material,
          quantidade: item.quantidade,
          classe: item.classe || 'Nenhum',
          valor_unitario: item.valor_unitario,
          resolve_forneceu: false,
          fornecido_forneceu: true
        }))

        const { error: itensError } = await supabase
          .from('itens_material')
          .insert(itensToInsert)

        if (itensError) throw itensError

        setSuccessMessage('Lista de material atualizada com sucesso!')
        setShowSuccessModal(true)
        
        // Limpar estado de edição
        setIsEditing(false)
        setEditingListaId(null)
        setEditingClienteId(null)
        setEditingOrdemServicoId(null)

      } else {
        // CRIAR NOVA LISTA
        setMessage('Criando nova lista de material...')
        
        // Criar lista de material
        const { data: listaData, error: listaError } = await supabase
          .from('lista_material')
          .insert({
            cliente_id: clienteId,
            id_ordem_servico: ordemServicoId,
            observacoes: `Lista de material - ${new Date().toLocaleDateString('pt-BR')}`,
            total_resolve: 0,
            total_fornecido: totalGeral
          })
          .select()
          .single()

        if (listaError) throw listaError

        // Inserir itens de material
        const itensToInsert = materialData.map((item) => ({
          lista_material_id: listaData.id,
          material: item.material,
          quantidade: item.quantidade,
          classe: item.classe || 'Nenhum',
          valor_unitario: item.valor_unitario,
          resolve_forneceu: false,
          fornecido_forneceu: true
        }))

        const { error: itensError } = await supabase
          .from('itens_material')
          .insert(itensToInsert)

        if (itensError) throw itensError

        setSuccessMessage('Nova lista de material criada com sucesso!')
        setShowSuccessModal(true)
      }

      // Limpar dados e recarregar listas
      setMaterialData([])
      setPreviewData([])
      setShowPreview(false)
      setSelectedCliente('')
      setSelectedOrdemServico('')
      setOsSearchTerm('')
      fetchListasExistentes()

    } catch (error) {
      console.error('Erro ao salvar lista:', error)
      setMessage(`Erro ao salvar: ${error.message}`)
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  // Função para carregar lista existente
  const handleLoadLista = async (listaId) => {
    try {
      setIsLoading(true)
      
      // Primeiro buscar informações da lista para obter o cliente_id e a OS
      const { data: listaInfo, error: listaError } = await supabase
        .from('lista_material')
        .select('cliente_id, id_ordem_servico')
        .eq('id', listaId)
        .single()

      if (listaError) throw listaError

      // Verificar se o cliente da lista pertence à empresa do usuário
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        const { data: clienteEmpresa, error: clienteError } = await supabase
          .from('clientes_cadastro')
          .select('empresa_id')
          .eq('id', listaInfo.cliente_id)
          .single()
        
        if (clienteError) throw clienteError
        
        if (clienteEmpresa.empresa_id !== userEmpresa.id) {
          setMessage('❌ Você não tem permissão para acessar esta lista de material.')
          setMessageType('error')
          return
        }
      }

      // Buscar os itens da lista
      const { data, error } = await supabase
        .from('itens_material')
        .select('*')
        .eq('lista_material_id', listaId)

      if (error) throw error

      // Configurar estado de edição
      setIsEditing(true)
      setEditingListaId(listaId)
      setEditingClienteId(listaInfo.cliente_id)
      setEditingOrdemServicoId(listaInfo.id_ordem_servico || null)
      setSelectedCliente(listaInfo.cliente_id)
      setSelectedOrdemServico(listaInfo.id_ordem_servico || '')
      const osVinculada = ordensServico.find(o => o.id === listaInfo.id_ordem_servico)
      setOsSearchTerm(osVinculada ? osVinculada.label : '')

      setMaterialData(data || [])
      setPreviewData(data || [])
      setShowPreview(true)
      setMessage('Lista carregada para edição!')
      setMessageType('success')

    } catch (error) {
      console.error('❌ Erro ao carregar lista:', error)
      setMessage(`Erro ao carregar lista: ${error.message}`)
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  // Função para abrir modal de confirmação de exclusão
  const handleDeleteLista = (listaId, nomeCliente) => {
    setListaToDelete({ id: listaId, nome: nomeCliente })
    setShowDeleteModal(true)
  }

  // Função para confirmar exclusão
  const confirmDeleteLista = async () => {
    if (!listaToDelete) return

    try {
      setIsLoading(true)
      setMessage('Excluindo lista de material...')
      setMessageType('info')

      // Verificar se o cliente da lista pertence à empresa do usuário
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        const { data: listaInfo, error: listaError } = await supabase
          .from('lista_material')
          .select('cliente_id')
          .eq('id', listaToDelete.id)
          .single()
        
        if (listaError) throw listaError
        
        const { data: clienteEmpresa, error: clienteError } = await supabase
          .from('clientes_cadastro')
          .select('empresa_id')
          .eq('id', listaInfo.cliente_id)
          .single()
        
        if (clienteError) throw clienteError
        
        if (clienteEmpresa.empresa_id !== userEmpresa.id) {
          setMessage('❌ Você não tem permissão para excluir esta lista de material.')
          setMessageType('error')
          return
        }
      }

      // Primeiro excluir os itens da lista
      const { error: itensError } = await supabase
        .from('itens_material')
        .delete()
        .eq('lista_material_id', listaToDelete.id)

      if (itensError) throw itensError

      // Depois excluir a lista principal
      const { error: listaError } = await supabase
        .from('lista_material')
        .delete()
        .eq('id', listaToDelete.id)

      if (listaError) throw listaError

      setMessage('Lista de material excluída com sucesso!')
      setMessageType('success')
      
      // Recarregar listas existentes
      fetchListasExistentes()

      // Se a lista excluída estava carregada, limpar a interface
      if (materialData.length > 0 && materialData[0]?.lista_material_id === listaToDelete.id) {
        setMaterialData([])
        setPreviewData([])
        setShowPreview(false)
        setSelectedCliente('')
        // Limpar estado de edição também
        setIsEditing(false)
        setEditingListaId(null)
        setEditingClienteId(null)
      }

    } catch (error) {
      console.error('Erro ao excluir lista:', error)
      setMessage(`Erro ao excluir lista: ${error.message}`)
      setMessageType('error')
    } finally {
      setIsLoading(false)
      setShowDeleteModal(false)
      setListaToDelete(null)
    }
  }

  // Função para cancelar exclusão
  const cancelDeleteLista = () => {
    setShowDeleteModal(false)
    setListaToDelete(null)
    setMessage('Exclusão cancelada.')
    setMessageType('info')
  }

  // Função para fechar modal de sucesso
  const closeSuccessModal = () => {
    setShowSuccessModal(false)
    setSuccessMessage('')
  }

  // Função para editar item (material e valor unitário vêm do catálogo — não editáveis aqui)
  const handleEditItem = (index, field, value) => {
    if (field === 'valor_unitario' || field === 'material') return
    const newData = [...materialData]
    newData[index][field] = value
    
    // Preservar o lista_material_id se estivermos editando
    if (isEditing && editingListaId && !newData[index].lista_material_id) {
      newData[index].lista_material_id = editingListaId
    }
    
    setMaterialData(newData)
    setPreviewData(newData)
  }

  // Função para remover item
  const handleRemoveItem = (index) => {
    const newData = materialData.filter((_, i) => i !== index)
    
    // Preservar o lista_material_id nos itens restantes se estivermos editando
    if (isEditing && editingListaId) {
      newData.forEach(item => {
        if (!item.lista_material_id) {
          item.lista_material_id = editingListaId
        }
      })
    }
    
    setMaterialData(newData)
    setPreviewData(newData)
  }

  // Função para adicionar item manualmente (linha vazia no preview)
  const handleAddItem = () => {
    const newItem = {
      material: '',
      quantidade: 1,
      classe: 'Nenhum',
      valor_unitario: 0,
      resolve_forneceu: false,
      fornecido_forneceu: true
    }
    
    // Se estamos editando, adicionar o ID da lista
    if (isEditing && editingListaId) {
      newItem.lista_material_id = editingListaId
    }
    
    setMaterialData([...materialData, newItem])
    setPreviewData([...previewData, newItem])
    setShowPreview(true)
  }

  // Função para recarregar clientes e ordens de serviço
  const handleReloadClientes = () => {
    fetchClientes()
    fetchOrdensServico()
  }

  const loadCatalogoPrecoItens = async () => {
    if (!userEmpresa?.id) {
      setCatalogoPrecoItens([])
      return
    }
    setCatalogoPrecoLoading(true)
    try {
      const { data, error } = await supabase
        .from('itens_material_preco')
        .select('id, material, preco_unitario, id_empresa')
        .eq('id_empresa', userEmpresa.id)
        .order('material', { ascending: true })
      if (error) throw error
      setCatalogoPrecoItens(data || [])
    } catch (err) {
      console.error('loadCatalogoPrecoItens:', err)
      setMessage(`Erro ao carregar catálogo: ${err.message || err}`)
      setMessageType('error')
      setCatalogoPrecoItens([])
    } finally {
      setCatalogoPrecoLoading(false)
    }
  }

  const openModalCatalogoPreco = () => {
    setCatalogoForm({ id: null, material: '', preco_unitario: '' })
    setShowModalCatalogoPreco(true)
    loadCatalogoPrecoItens()
  }

  const closeModalCatalogoPreco = () => {
    setShowModalCatalogoPreco(false)
    setCatalogoForm({ id: null, material: '', preco_unitario: '' })
  }

  const handleEditCatalogoPreco = (row) => {
    setCatalogoForm({
      id: row.id,
      material: row.material || '',
      preco_unitario: row.preco_unitario != null ? String(row.preco_unitario) : ''
    })
  }

  const handleSaveCatalogoPreco = async () => {
    const mat = (catalogoForm.material || '').trim()
    if (!mat) {
      setMessage('Informe o nome do material.')
      setMessageType('warning')
      return
    }
    if (!userEmpresa?.id) {
      setMessage('Empresa do usuário não encontrada.')
      setMessageType('error')
      return
    }
    const precoRaw = String(catalogoForm.preco_unitario || '').replace(',', '.').trim()
    const preco = precoRaw === '' ? 0 : parseFloat(precoRaw)
    if (Number.isNaN(preco) || preco < 0) {
      setMessage('Preço unitário inválido.')
      setMessageType('warning')
      return
    }

    setCatalogoPrecoSaving(true)
    try {
      if (catalogoForm.id) {
        const { error } = await supabase
          .from('itens_material_preco')
          .update({ material: mat, preco_unitario: preco })
          .eq('id', catalogoForm.id)
          .eq('id_empresa', userEmpresa.id)
        if (error) throw error
        setMessage('Material atualizado no catálogo.')
      } else {
        const { error } = await supabase.from('itens_material_preco').insert({
          material: mat,
          preco_unitario: preco,
          id_empresa: userEmpresa.id
        })
        if (error) throw error
        setMessage('Material incluído no catálogo.')
      }
      setMessageType('success')
      setCatalogoForm({ id: null, material: '', preco_unitario: '' })
      await loadCatalogoPrecoItens()
    } catch (err) {
      console.error('handleSaveCatalogoPreco:', err)
      setMessage(err.message || 'Erro ao salvar material.')
      setMessageType('error')
    } finally {
      setCatalogoPrecoSaving(false)
    }
  }

  const handleDeleteCatalogoPreco = async (id) => {
    if (!window.confirm('Excluir este material do catálogo de preços?')) return
    setCatalogoPrecoSaving(true)
    try {
      const { error } = await supabase
        .from('itens_material_preco')
        .delete()
        .eq('id', id)
        .eq('id_empresa', userEmpresa.id)
      if (error) throw error
      if (catalogoForm.id === id) {
        setCatalogoForm({ id: null, material: '', preco_unitario: '' })
      }
      setMessage('Material removido do catálogo.')
      setMessageType('success')
      await loadCatalogoPrecoItens()
    } catch (err) {
      console.error('handleDeleteCatalogoPreco:', err)
      setMessage(err.message || 'Erro ao excluir.')
      setMessageType('error')
    } finally {
      setCatalogoPrecoSaving(false)
    }
  }

  // Função para exportar PDF (todos os itens da lista)
  const handleExportPDF = async (listaId, nomeCliente) => {
    try {
      setIsLoading(true)
      setMessage('Gerando PDF...')
      setMessageType('info')

      // Verificar se o cliente da lista pertence à empresa do usuário
      if (!podeVerTodosRegistrosListaMaterial && userEmpresa?.id) {
        const { data: listaInfo, error: listaError } = await supabase
          .from('lista_material')
          .select('cliente_id')
          .eq('id', listaId)
          .single()
        
        if (listaError) throw listaError
        
        const { data: clienteEmpresa, error: clienteError } = await supabase
          .from('clientes_cadastro')
          .select('empresa_id')
          .eq('id', listaInfo.cliente_id)
          .single()
        
        if (clienteError) throw clienteError
        
        if (clienteEmpresa.empresa_id !== userEmpresa.id) {
          setMessage('❌ Você não tem permissão para exportar esta lista de material.')
          setMessageType('error')
          return
        }
      }

      // Buscar itens da lista
      const { data: itens, error: itensError } = await supabase
        .from('itens_material')
        .select('*')
        .eq('lista_material_id', listaId)

      if (itensError) throw itensError

      const materiaisFornecidos = itens && itens.length > 0 ? itens : []

      if (materiaisFornecidos.length === 0) {
        setMessage('Nenhum item nesta lista para exportar.')
        setMessageType('warning')
        return
      }

      const totalFornecido = materiaisFornecidos.reduce((total, item) => {
        return total + (Number(item.valor_unitario) || 0) * (Number(item.quantidade) || 0)
      }, 0)

      // Criar conteúdo do PDF
      const pdfContent = {
        cliente: nomeCliente,
        data: new Date().toLocaleDateString('pt-BR'),
        itens: materiaisFornecidos,
        total: totalFornecido
      }

      // Garantir que o PDF use sempre a empresa do usuário logado
      let empresa = empresaData
      if (!empresa && userEmpresa?.id) {
        const { data: emp, error: empErr } = await supabase
          .from('empresa')
          .select('empresa, razao_social, cnpj, endereco, email, contato, cidade, estado, cep')
          .eq('id', userEmpresa.id)
          .single()
        if (!empErr && emp) empresa = emp
      }
      if (!empresa) {
        setMessage('Não foi possível carregar os dados da empresa para o PDF.')
        setMessageType('error')
        return
      }

      // Gerar PDF usando jsPDF
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF()

      // Configurar fonte para caracteres especiais
      doc.setFont('helvetica')
      
      // Configurações de estilo profissional
      const primaryColor = [33, 33, 33] // Cor principal escura
      const secondaryColor = [66, 66, 66] // Cor secundária
      const accentColor = [0, 123, 255] // Cor de destaque
      const borderColor = [200, 200, 200] // Cor das bordas

      // Cabeçalho da empresa (lado esquerdo) - dados da empresa do usuário logado

      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...primaryColor)
      // Usar razão social ou nome fantasia da empresa do usuário logado
      const nomeEmpresa = (empresa.razao_social && empresa.razao_social.trim() !== '') 
        ? empresa.razao_social 
        : (empresa.empresa || '')
      doc.text(nomeEmpresa, 20, 20)
      
      // Linha decorativa sob o nome da empresa
      doc.setDrawColor(...accentColor)
      doc.setLineWidth(0.5)
      doc.line(20, 22, 100, 22)
      
      // Informações da empresa (lado esquerdo) - Estilo organizado
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...secondaryColor)
      
      let yPosition = 28
      
      // CNPJ com ícone visual
      if (empresa.cnpj) {
        doc.setFillColor(...accentColor)
        doc.circle(18, yPosition, 1, 'F')
        doc.text(`CNPJ: ${empresa.cnpj}`, 22, yPosition + 2)
        yPosition += 5
      }
      
      // Endereço com ícone - exibir se existir e tiver conteúdo
      if (empresa.endereco && empresa.endereco.trim() !== '') {
        doc.circle(18, yPosition, 1, 'F')
        doc.text(empresa.endereco, 22, yPosition + 2)
        yPosition += 5
      }
      
      // Município e Estado
      if (empresa.cidade || empresa.estado) {
        doc.circle(18, yPosition, 1, 'F')
        const cidadeEstado = `${empresa.cidade || ''} - ${empresa.estado || ''}`.replace(/^ - | - $/, '')
        doc.text(cidadeEstado, 22, yPosition + 2)
        yPosition += 5
      }
      
      // CEP
      if (empresa.cep) {
        doc.circle(18, yPosition, 1, 'F')
        doc.text(`CEP: ${empresa.cep}`, 22, yPosition + 2)
        yPosition += 5
      }
      
      // Telefone com ícone
      if (empresa.contato) {
        doc.circle(18, yPosition, 1, 'F')
        doc.text(`Tel: ${empresa.contato}`, 22, yPosition + 2)
        yPosition += 5
      }
      
      // E-mail com ícone
      if (empresa.email) {
        doc.circle(18, yPosition, 1, 'F')
        doc.text(`E-mail: ${empresa.email}`, 22, yPosition + 2)
        yPosition += 5
      }

      // Função para quebrar texto em múltiplas linhas
      const wrapText = (text, maxWidth) => {
        const words = text.split(' ')
        const lines = []
        let currentLine = ''
        
        words.forEach(word => {
          const testLine = currentLine + (currentLine ? ' ' : '') + word
          if (testLine.length <= maxWidth) {
            currentLine = testLine
          } else {
            if (currentLine) lines.push(currentLine)
            currentLine = word
          }
        })
        if (currentLine) lines.push(currentLine)
        
        return lines
      }

      // Informações do cliente (lado direito) - Design profissional
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...accentColor)
      doc.text(`ORÇAMENTO ${empresa.empresa || 'TECSOL'}`, 120, 20)
      
      // Linha decorativa sob o título
      doc.setDrawColor(...accentColor)
      doc.setLineWidth(0.3)
      doc.line(120, 22, 180, 22)
      
      // Cliente com quebra de linha automática (máximo 25 caracteres por linha)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...primaryColor)
      const clienteLines = wrapText(pdfContent.cliente, 25)
      clienteLines.forEach((line, lineIndex) => {
        doc.text(`${lineIndex === 0 ? 'Cliente: ' : ''}${line}`, 120, 35 + (lineIndex * 5))
      })
      
      // Ajustar posição da data baseado no número de linhas do cliente
      const dataPosition = 35 + (clienteLines.length * 5) + 5
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(...secondaryColor)
      doc.text(`Data: ${pdfContent.data}`, 120, dataPosition)

      // Calcular posição da linha separadora baseado no tamanho do cabeçalho
      const separatorPosition = Math.max(60, dataPosition + 10)
      
      // Linha separadora principal - Estilo profissional
      doc.setDrawColor(...borderColor)
      doc.setLineWidth(1)
      doc.line(20, separatorPosition, 190, separatorPosition)
      
      // Linha decorativa dupla
      doc.setDrawColor(...accentColor)
      doc.setLineWidth(0.3)
      doc.line(20, separatorPosition + 2, 190, separatorPosition + 2)

      // Cabeçalho da tabela - Design aprimorado
      const tableHeaderPosition = separatorPosition + 10
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...primaryColor)
      
      // Fundo do cabeçalho da tabela
      doc.setFillColor(245, 245, 245)
      doc.rect(20, tableHeaderPosition - 3, 170, 8, 'F')
      
      // Texto do cabeçalho
      doc.text('Material', 20, tableHeaderPosition)
      doc.text('Qtd.', 110, tableHeaderPosition)
      doc.text('Valor Unit.', 130, tableHeaderPosition)
      doc.text('Valor Total', 160, tableHeaderPosition)

      // Linha separadora da tabela
      doc.setDrawColor(...borderColor)
      doc.setLineWidth(0.5)
      doc.line(20, tableHeaderPosition + 5, 190, tableHeaderPosition + 5)

      // Itens
      yPosition = tableHeaderPosition + 15
             pdfContent.itens.forEach((item, index) => {
         if (yPosition > 250) {
           doc.addPage()
           yPosition = 20
         }



        // Material com quebra de linha automática (máximo 25 caracteres por linha)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...primaryColor)
        const materialLines = wrapText(item.material, 25)
        materialLines.forEach((line, lineIndex) => {
          doc.text(line, 20, yPosition + (lineIndex * 4))
        })
        
        // Quantidade (posicionada mais à direita, alinhada com o cabeçalho)
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...accentColor)
        doc.text(item.quantidade.toString(), 110, yPosition)
        
        // Valor Unitário
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...secondaryColor)
        doc.text(`R$ ${formatNumber(item.valor_unitario)}`, 130, yPosition)
        
        // Valor Total
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...primaryColor)
        doc.text(`R$ ${formatNumber(item.valor_unitario * item.quantidade)}`, 160, yPosition)

        // Ajustar posição Y baseado no número de linhas do material
        yPosition += Math.max(10, materialLines.length * 4 + 2)
      })

      // Linha separadora antes do total - Estilo profissional
      doc.setDrawColor(...borderColor)
      doc.setLineWidth(1)
      doc.line(20, yPosition + 5, 190, yPosition + 5)
      
      // Linha decorativa dupla
      doc.setDrawColor(...accentColor)
      doc.setLineWidth(0.3)
      doc.line(20, yPosition + 7, 190, yPosition + 7)

      // Total - Design destacado
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...accentColor)
      
      // Fundo do total
      doc.setFillColor(245, 245, 245)
      doc.rect(130, yPosition + 10, 60, 8, 'F')
      
      // Texto do total
      doc.text(`TOTAL: R$ ${formatNumber(pdfContent.total)}`, 140, yPosition + 15)

      // Rodapé profissional
      const footerY = 280
      doc.setDrawColor(...borderColor)
      doc.setLineWidth(0.5)
      doc.line(20, footerY, 190, footerY)
      
      // Informações do rodapé
      doc.setFontSize(8)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(...secondaryColor)
      doc.text('Documento gerado automaticamente pelo Sistema TecSol', 105, footerY + 5, { align: 'center' })
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 105, footerY + 10, { align: 'center' })
      
      // Salvar PDF
      const empresaNome = (empresa.razao_social || empresa.empresa || 'TECSOL').replace(/[^a-zA-Z0-9]/g, '_')
      const fileName = `orcamento_${empresaNome}_${nomeCliente.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

              setMessage(`PDF exportado com sucesso! ${materiaisFornecidos.length} itens incluídos.`)
      setMessageType('success')

    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
      setMessage(`Erro ao exportar PDF: ${error.message}`)
      setMessageType('error')
    } finally {
      setIsLoading(false)
    }
  }

  const { totalGeral } = calculateTotals()

  return (
    <div className="lista-material-container">
      <div className="lista-material-header">
        <h2>📋 Sistema de Lista de Material</h2>
        <div className="lista-material-header-actions">
          {isAdministrador && (
            <button
              type="button"
              className="catalogo-preco-button"
              onClick={openModalCatalogoPreco}
              disabled={isLoading}
              title="Cadastrar ou editar materiais e preços (itens_material_preco)"
            >
              🏷️ Catálogo de preços
            </button>
          )}
          <button className="back-button" onClick={onBack}>
            ← Voltar
          </button>
        </div>
      </div>

      {/* Mensagens */}
      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      {/* Indicador de Edição */}
      {isEditing && (
        <div className="editing-indicator">
          <h3>✏️ Editando Lista Existente</h3>
          <p>Você está editando uma lista existente. As alterações serão aplicadas à lista atual.</p>
        </div>
      )}

      {/* Seleção de Ordem de Serviço */}
      <div className="form-section">
        <h3>🧾 Selecionar Ordem de Serviço</h3>
        <div className="cliente-controls">
          <div className="client-search-container">
            <input
              type="text"
              value={osSearchTerm}
              onChange={(e) => handleOsSearch(e.target.value)}
              onFocus={handleOsSearchFocus}
              onBlur={handleOsSearchBlur}
              onKeyDown={handleOsSearchKeyDown}
              placeholder="Busque pela OS (nº, cliente ou tipo de serviço)..."
              className="client-search-input"
              disabled={isLoading || isEditing}
            />
            {showOsSuggestions && (
              <div className="client-suggestions">
                {filteredOrdens.map(os => (
                  <div
                    key={os.id}
                    className="client-suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleOsSelect(os)
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      handleOsSelect(os)
                    }}
                  >
                    {os.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            className="reload-button"
            onClick={handleReloadClientes}
            disabled={isLoading}
            title="Recarregar ordens de serviço"
          >
            🔄
          </button>
        </div>

        {isEditing && (
          <div className="cliente-info">
            <p><strong>Cliente:</strong> {clientes.find(c => c.id === editingClienteId)?.nome_cliente}</p>
            {editingOrdemServicoId
              ? <p><strong>Ordem de serviço:</strong> {ordensServico.find(o => o.id === editingOrdemServicoId)?.label || `OS #${editingOrdemServicoId}`}</p>
              : <p><em>Esta lista ainda não está vinculada a uma ordem de serviço.</em></p>}
            <p><em>A ordem de serviço não pode ser alterada durante a edição. Use o botão "Nova Lista" para criar uma lista a partir de outra OS.</em></p>
          </div>
        )}

        {ordensServico.length === 0 && !isLoading && (
          <div className="no-clients-message">
            <p>Nenhuma ordem de serviço encontrada. Verifique:</p>
            <ul>
              <li>Se você está logado corretamente</li>
              <li>Se existem ordens de serviço cadastradas para a empresa</li>
              <li>Se você tem permissão para ver as ordens de serviço</li>
            </ul>
            <button
              className="retry-button"
              onClick={handleReloadClientes}
              disabled={isLoading}
            >
              🔄 Tentar Novamente
            </button>
          </div>
        )}
      </div>

             {/* Adicionar Item Manualmente */}
       <div className="form-section">
         <h3>✏️ Adicionar Item Manualmente</h3>
         <div className="manual-controls">
           <button 
             className="add-button"
             onClick={handleAddItem}
             disabled={isLoading}
           >
             + Adicionar Item
           </button>
         </div>
         <div className="material-search-below-button" ref={materialSearchRef}>
           <div className="material-search-inline-row">
             <div className="material-search-qty-group">
               <label className="material-search-label" htmlFor="manual-material-qty">
                 Quantidade
               </label>
               <input
                 id="manual-material-qty"
                 type="number"
                 min={1}
                 value={manualMaterialQuantidade}
                 onChange={(e) => {
                   const v = parseInt(e.target.value, 10)
                   setManualMaterialQuantidade(Number.isFinite(v) && v >= 1 ? v : 1)
                 }}
                 className="material-search-input-below material-qty-input"
                 title="Quantidade ao adicionar pela busca (catálogo itens_material_preco)"
               />
             </div>
             <div className="material-search-field-wrap">
               <label className="material-search-label" htmlFor="manual-material-search">
                 Buscar material na base (adiciona na Preview)
               </label>
               <input
                 id="manual-material-search"
                 type="text"
                 value={materialSearchTerm}
                 onFocus={handleMaterialSearchFocus}
                 onChange={(e) => handleMaterialSearchChange(e.target.value)}
                 placeholder="Buscar material do catálogo da sua empresa..."
                 className="material-search-input-below"
                 autoComplete="off"
               />
               {showMaterialDropdown && (
                 <div className="material-search-dropdown material-search-dropdown-below">
                   {materialSearchLoading ? (
                     <div className="material-search-loading">Carregando...</div>
                   ) : materialSuggestions.length === 0 ? (
                     <div className="material-search-empty">Nenhum material encontrado no catálogo.</div>
                   ) : (
                     materialSuggestions.map((opt, i) => (
                       <div
                         key={`${opt.material}-${i}`}
                         className="material-search-option"
                         onClick={() => handleAddMaterialFromSearch(opt.material, opt.valor_unitario)}
                       >
                         <span className="material-search-option-name">{opt.material}</span>
                         <span className="material-search-option-valor">
                           {opt.valor_unitario != null && opt.valor_unitario > 0
                             ? formatCurrency(opt.valor_unitario)
                             : '–'}
                         </span>
                       </div>
                     ))
                   )}
                 </div>
               )}
             </div>
           </div>
         </div>
       </div>

      {/* Preview dos Dados */}
      {showPreview && (
        <div className="form-section">
          <h3>👁️ Preview dos Dados</h3>
          
          {/* Resumo dos Totais */}
          <div className="totals-summary">
            <h4>💰 Resumo dos Valores</h4>
            <div className="totals-grid">
              <div className="total-item total-geral">
                <span className="total-label">Total Geral:</span>
                <span className="total-value">{formatCurrency(totalGeral)}</span>
              </div>
            </div>
          </div>

          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Quantidade</th>
                  <th>Valor Unit.</th>
                  <th>Valor Total</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((item, index) => (
                  <tr key={index}>
                    <td className="preview-material-readonly" title={item.material || ''}>
                      {item.material || '—'}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.quantidade}
                        onChange={(e) => handleEditItem(index, 'quantidade', parseInt(e.target.value) || 1)}
                        className="table-input"
                        min="1"
                      />
                    </td>
                    <td className="preview-valor-unit">
                      {formatCurrency(Number(item.valor_unitario) || 0)}
                    </td>
                    <td className="valor-total">
                      {formatCurrency((Number(item.valor_unitario) || 0) * (Number(item.quantidade) || 0))}
                    </td>
                    <td>
                      <button
                        onClick={() => handleRemoveItem(index)}
                        className="remove-button"
                        disabled={isLoading}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Botões de Ação */}
      {showPreview && (
        <div className="form-section">
          <div className="action-buttons">
            <button
              className="save-button"
              onClick={handleSaveLista}
              disabled={isLoading || !selectedCliente || materialData.length === 0}
            >
              {isLoading ? 'Salvando...' : isEditing ? '💾 Atualizar Lista' : '💾 Salvar Nova Lista'}
            </button>
            
            {isEditing && (
              <button
                className="new-list-button"
                onClick={() => {
                  setIsEditing(false)
                  setEditingListaId(null)
                  setEditingClienteId(null)
                  setEditingOrdemServicoId(null)
                  setSelectedCliente('')
                  setSelectedOrdemServico('')
                  setOsSearchTerm('')
                  setMessage('Modo de nova lista ativado. Selecione uma ordem de serviço para criar uma nova lista.')
                  setMessageType('info')
                }}
                disabled={isLoading}
                title="Criar nova lista em vez de editar a atual"
              >
                📝 Nova Lista
              </button>
            )}
            
            <button
              className="clear-button"
              onClick={() => {
                setMaterialData([])
                setPreviewData([])
                setShowPreview(false)
                // Limpar estado de edição
                setIsEditing(false)
                setEditingListaId(null)
                setEditingClienteId(null)
                setEditingOrdemServicoId(null)
                setSelectedCliente('')
                setSelectedOrdemServico('')
                setOsSearchTerm('')
              }}
              disabled={isLoading}
            >
              🗑️ Limpar Dados
            </button>
          </div>
        </div>
      )}

      {/* Listas Existentes */}
      <div className="form-section">
        <h3>📚 Listas Existentes</h3>
        <button
          className="toggle-button"
          onClick={() => setShowExistingListas(!showExistingListas)}
        >
          {showExistingListas ? '🔽 Ocultar' : '🔼 Mostrar'} Listas Existentes
        </button>
        
        {showExistingListas && (
          <div className="existing-listas">
            {listasExistentes.length === 0 ? (
              <p>Nenhuma lista encontrada.</p>
            ) : (
              listasExistentes.map(lista => (
                <div key={lista.id} className="lista-item">
                  <div className="lista-info">
                    <strong>Cliente:</strong> {lista.clientes?.nome_cliente}
                    <br />
                    <strong>Ordem de serviço:</strong> {lista.id_ordem_servico ? `OS #${lista.id_ordem_servico}` : 'Não vinculada'}
                    <br />
                    <strong>Data:</strong> {new Date(lista.data_criacao).toLocaleDateString('pt-BR')}
                    {lista.observacoes && (
                      <>
                        <br />
                        <strong>Observações:</strong> {lista.observacoes}
                      </>
                    )}
                    {lista.total_fornecido != null && (
                      <>
                        <br />
                        <strong>Total:</strong> {formatCurrency(lista.total_fornecido)}
                      </>
                    )}
                  </div>
                  <div className="lista-item-actions">
                     <button
                       className="load-button"
                       onClick={() => handleLoadLista(lista.id)}
                       disabled={isLoading}
                       title="Carregar Lista"
                     >
                       📥 Carregar
                     </button>
                     
                     <button
                       className="export-button"
                       onClick={() => handleExportPDF(lista.id, lista.clientes?.nome_cliente)}
                       disabled={isLoading}
                       title="Exportar Orçamento Fornecido (PDF)"
                     >
                       📄 Exportar PDF
                     </button>
                     
                     {(userRole === 'administrador' || userRole === 'administrativo') && (
                       <button
                         className="delete-button"
                         onClick={() => handleDeleteLista(lista.id, lista.clientes?.nome_cliente)}
                         disabled={isLoading}
                         title="Excluir Lista"
                       >
                         🗑️ Excluir
                       </button>
                     )}
                   </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal catálogo itens_material_preco (somente administrador) */}
      {showModalCatalogoPreco && isAdministrador && (
        <div className="loading-modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && closeModalCatalogoPreco()}>
          <div className="modal-catalogo-preco" role="dialog" aria-labelledby="modal-catalogo-preco-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-catalogo-preco-header">
              <h3 id="modal-catalogo-preco-title">🏷️ Catálogo de materiais e preços</h3>
              <button type="button" className="modal-catalogo-preco-close" onClick={closeModalCatalogoPreco} aria-label="Fechar">
                ×
              </button>
            </div>
            <p className="modal-catalogo-preco-hint">
              Tabela <code>itens_material_preco</code> — empresa: {userEmpresa?.empresa || userEmpresa?.id || '—'}
            </p>
            {!userEmpresa?.id ? (
              <p className="modal-catalogo-preco-warning">Não foi possível identificar a empresa do perfil.</p>
            ) : (
              <>
                <div className="modal-catalogo-preco-form">
                  <div className="modal-catalogo-preco-field">
                    <label htmlFor="catalogo-material">Material</label>
                    <input
                      id="catalogo-material"
                      type="text"
                      value={catalogoForm.material}
                      onChange={(e) => setCatalogoForm((f) => ({ ...f, material: e.target.value }))}
                      placeholder="Nome do material"
                      maxLength={255}
                      disabled={catalogoPrecoSaving}
                    />
                  </div>
                  <div className="modal-catalogo-preco-field modal-catalogo-preco-field-preco">
                    <label htmlFor="catalogo-preco">Preço unitário (R$)</label>
                    <input
                      id="catalogo-preco"
                      type="text"
                      inputMode="decimal"
                      value={catalogoForm.preco_unitario}
                      onChange={(e) => setCatalogoForm((f) => ({ ...f, preco_unitario: e.target.value }))}
                      placeholder="0,00"
                      disabled={catalogoPrecoSaving}
                    />
                  </div>
                  <div className="modal-catalogo-preco-form-actions">
                    <button
                      type="button"
                      className="catalogo-preco-button catalogo-preco-button-primary"
                      onClick={handleSaveCatalogoPreco}
                      disabled={catalogoPrecoSaving}
                    >
                      {catalogoPrecoSaving ? 'Salvando...' : catalogoForm.id ? '💾 Atualizar' : '➕ Incluir'}
                    </button>
                    {catalogoForm.id != null && (
                      <button
                        type="button"
                        className="catalogo-preco-button catalogo-preco-button-secondary"
                        onClick={() => setCatalogoForm({ id: null, material: '', preco_unitario: '' })}
                        disabled={catalogoPrecoSaving}
                      >
                        Novo (limpar)
                      </button>
                    )}
                  </div>
                </div>

                <div className="modal-catalogo-preco-table-wrap">
                  {catalogoPrecoLoading ? (
                    <p className="modal-catalogo-preco-loading">Carregando...</p>
                  ) : (
                    <table className="modal-catalogo-preco-table">
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th>Preço unit.</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {catalogoPrecoItens.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="modal-catalogo-preco-empty">
                              Nenhum item cadastrado para esta empresa.
                            </td>
                          </tr>
                        ) : (
                          catalogoPrecoItens.map((row) => (
                            <tr key={row.id}>
                              <td>{row.material}</td>
                              <td>{formatCurrency(Number(row.preco_unitario) || 0)}</td>
                              <td className="modal-catalogo-preco-row-actions">
                                <button
                                  type="button"
                                  className="catalogo-preco-link"
                                  onClick={() => handleEditCatalogoPreco(row)}
                                  disabled={catalogoPrecoSaving}
                                >
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  className="catalogo-preco-link catalogo-preco-link-danger"
                                  onClick={() => handleDeleteCatalogoPreco(row.id)}
                                  disabled={catalogoPrecoSaving}
                                >
                                  Excluir
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
            <div className="modal-catalogo-preco-footer">
              <button type="button" className="back-button" onClick={closeModalCatalogoPreco}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="loading-modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="delete-modal-content">
              <div className="delete-modal-header">
                <h3>⚠️ Confirmar Exclusão</h3>
              </div>
              
              <div className="delete-modal-body">
                <p className="delete-warning">
                  <strong>ATENÇÃO:</strong> Esta ação não pode ser desfeita!
                </p>
                <p>
                  Tem certeza que deseja <strong>EXCLUIR</strong> a lista de material do cliente:
                </p>
                <p className="delete-client-name">
                  <strong>"{listaToDelete?.nome}"</strong>
                </p>
              </div>
              
              <div className="delete-modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={cancelDeleteLista}
                  disabled={isLoading}
                >
                  ❌ Não
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteLista}
                  disabled={isLoading}
                >
                  {isLoading ? 'Excluindo...' : '✅ Sim, Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="loading-modal-overlay">
          <div className="success-modal">
            <div className="success-modal-content">
              <div className="success-modal-header">
                <h3>✅ Sucesso!</h3>
              </div>
              
              <div className="success-modal-body">
                <div className="success-icon">
                  🎉
                </div>
                <p className="success-message">
                  {successMessage}
                </p>
                <p className="success-subtitle">
                  A lista foi salva com sucesso e os totais foram calculados automaticamente.
                </p>
              </div>
              
              <div className="success-modal-footer">
                <button
                  className="btn btn-success"
                  onClick={closeSuccessModal}
                >
                  👍 Entendi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ListaMaterial
